<template>
	<AnalyticsPanel
		title="Top content"
		:subtitle="subtitle"
		:state="state"
		:padded="false"
		body-class="pb-2"
		min-height="240px"
		:empty-title="emptyTitle"
		:empty-text="emptyText"
		@retry="$emit('retry')"
	>
		<template #header>
			<h2 class="mb-0 text-base font-semibold leading-6 text-surface-text-2">Top content</h2>
			<p v-if="subtitle" class="mb-0 mt-0.5 text-xs leading-4 text-surface-7">
				{{ subtitle }}<template v-if="subtitleLink">
					·
					<button type="button" class="text-surface-8 underline decoration-surface-6 underline-offset-2 hover:text-white" data-testid="analytics-leaderboard-choose" @click="$emit('subtitle-link')">{{ subtitleLink }}</button>
				</template>
			</p>
		</template>
		<template #skeleton>
			<div class="px-4 md:px-5">
				<div v-for="i in 5" :key="i" class="flex items-center gap-3 border-b border-white/[0.04] py-3 last:border-0">
					<div class="h-4 w-4 animate-pulse rounded bg-surface-3/70" />
					<div class="flex flex-1 flex-col gap-1.5">
						<div class="h-4 w-1/2 animate-pulse rounded bg-surface-3/80" />
						<div class="h-3 w-12 animate-pulse rounded bg-surface-3/60" />
					</div>
					<div class="hidden h-4 w-16 animate-pulse rounded bg-surface-3/60 lg:block" />
					<div class="hidden h-4 w-16 animate-pulse rounded bg-surface-3/60 lg:block" />
				</div>
			</div>
		</template>

		<!-- Desktop table: fixed layout so the Content column takes whatever the numeric columns (fixed width, right-aligned) leave, never below 220px. -->
		<div class="hidden lg:block overflow-x-auto">
			<table class="w-full min-w-[868px] table-fixed border-collapse text-sm" data-testid="analytics-leaderboard">
				<colgroup>
					<col class="w-12">
					<col class="min-w-[220px]">
					<col v-for="col in columns" :key="col.key" class="w-[120px]">
				</colgroup>
				<thead>
					<tr class="text-[11px] uppercase tracking-wide text-surface-7">
						<th scope="col" class="py-2 pl-5 pr-1 text-left font-medium">#</th>
						<!-- Same left padding as the cell so the label sits over the names. -->
						<th scope="col" class="py-2 pl-2 pr-3 text-left font-medium">Content</th>
						<th
							v-for="col in columns"
							:key="col.key"
							scope="col"
							class="px-3 py-2 text-right font-medium whitespace-nowrap"
							:aria-sort="sort.column === col.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'"
						>
							<button
								type="button"
								class="inline-flex items-center gap-1 rounded px-1 -mx-1 hover:text-white"
								:class="sort.column === col.key ? 'text-white' : ''"
								:title="isColumnFailed(col.key) ? `${col.label} couldn't be loaded. Click to retry.` : undefined"
								:aria-label="isColumnFailed(col.key) ? `Retry loading ${col.label}` : undefined"
								@click="onSort(col.key)"
							>
								{{ col.label }}
								<!-- While a grouped request has failed the sort glyph becomes a retry glyph; width stays put. -->
								<svg v-if="isColumnFailed(col.key)" width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true" class="shrink-0 text-[#F59E0B]">
									<path d="M10 6a4 4 0 1 1-1.2-2.85M10 1.5v2.2H7.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
								</svg>
								<svg v-else width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" class="shrink-0" :class="sort.column === col.key ? 'opacity-100' : 'opacity-30'">
									<path v-if="sort.column === col.key && sort.direction === 'asc'" d="M5 8V2M5 2L2.5 4.5M5 2l2.5 2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
									<path v-else d="M5 2v6M5 8L2.5 5.5M5 8l2.5-2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
								</svg>
							</button>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(row, index) in rows"
						:key="row.id"
						class="group cursor-pointer border-t border-white/[0.04] transition-colors hover:bg-surface-3/40 focus-within:bg-surface-3/40"
						@click="$emit('open', row)"
					>
						<td class="py-2 pl-5 pr-1 text-surface-7 tabular-nums">{{ index + 1 }}</td>
						<td class="py-2 pl-2 pr-3">
							<button type="button" class="flex w-full min-w-0 items-center gap-3 text-left focus:outline-none" @click.stop="$emit('open', row)">
								<!-- Poster hook: renders nothing until a row carries thumbnailUrl (or the slot is filled). -->
								<slot name="thumbnail" :row="row">
									<img v-if="row.thumbnailUrl" :src="row.thumbnailUrl" alt="" loading="lazy" class="h-9 w-16 shrink-0 rounded object-cover ring-1 ring-inset ring-white/[0.05]">
								</slot>
								<span class="flex min-w-0 flex-1 flex-col">
									<span v-if="row.nameResolving" class="my-0.5 block h-4 w-32 animate-pulse rounded bg-surface-4" aria-busy="true" aria-label="Loading name" data-testid="analytics-name-skeleton" />
									<span v-else class="truncate text-sm font-medium leading-5 text-surface-text-2 group-hover:text-white" :title="row.name">{{ row.name }}</span>
									<span class="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] leading-4 text-surface-7">
										<AnalyticsTypeBadge :type="row.type" />
										<span v-if="row.folderName" class="truncate" :title="row.folderName">{{ row.folderName }}</span>
									</span>
								</span>
							</button>
						</td>
						<td v-for="col in columns" :key="col.key" class="px-3 py-2 text-right tabular-nums text-surface-text-1 whitespace-nowrap">
							<template v-if="isColumnLoading(col.key)">
								<span class="inline-block h-4 w-12 animate-pulse rounded bg-surface-3/80 align-middle" aria-label="Loading" />
							</template>
							<template v-else-if="hasValue(row, col.key)">{{ format(col.key, row.metrics[col.key]) }}</template>
							<span v-else class="text-surface-6" :title="pendingTitle(col.key)">--</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Mobile stacked rows -->
		<ul class="lg:hidden divide-y divide-white/[0.04]">
			<li v-for="(row, index) in rows" :key="row.id">
				<button type="button" class="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-3/40" @click="$emit('open', row)">
					<span class="w-4 shrink-0 text-xs text-surface-7 tabular-nums">{{ index + 1 }}</span>
					<slot name="thumbnail" :row="row">
						<img v-if="row.thumbnailUrl" :src="row.thumbnailUrl" alt="" loading="lazy" class="h-9 w-16 shrink-0 rounded object-cover ring-1 ring-inset ring-white/[0.05]">
					</slot>
					<span class="min-w-0 flex-1">
						<span v-if="row.nameResolving" class="my-0.5 block h-4 w-32 animate-pulse rounded bg-surface-4" aria-busy="true" aria-label="Loading name" data-testid="analytics-name-skeleton" />
						<span v-else class="block truncate text-sm font-medium text-surface-text-2" :title="row.name">{{ row.name }}</span>
						<span class="mt-0.5 flex items-center gap-1.5 text-[11px] leading-4 text-surface-7">
							<AnalyticsTypeBadge :type="row.type" />
							<span v-if="hasValue(row, 'play')" class="truncate">{{ format('play', row.metrics.play) }} plays</span>
						</span>
					</span>
					<span class="shrink-0 text-right">
						<span class="block text-sm font-medium tabular-nums text-surface-text-1">
							<template v-if="hasValue(row, 'totalHoursWatched')">{{ format('totalHoursWatched', row.metrics.totalHoursWatched) }}</template>
							<span v-else class="text-surface-6">--</span>
						</span>
						<span class="block text-[11px] leading-4 text-surface-7">watch time</span>
					</span>
				</button>
			</li>
		</ul>

		<p v-if="note" class="mb-0 px-4 pt-3 text-[11px] leading-4 text-surface-6 md:px-5">{{ note }}</p>
	</AnalyticsPanel>
</template>

<script setup>
import AnalyticsPanel from './AnalyticsPanel.vue';
import AnalyticsTypeBadge from './AnalyticsTypeBadge.vue';
import { LEADERBOARD_COLUMNS } from './analyticsMetrics';
import { formatMetricValue } from './analyticsFormatters';

const props = defineProps({
	// [{ id, name, nameResolving?, type, folderName?, thumbnailUrl?, metrics: { [column]: number|null } }]
	// nameResolving: the stores have not named this id yet -> skeleton instead of a raw id.
	// thumbnailUrl (or the `thumbnail` scoped slot) shows a poster; absent -> nothing rendered.
	rows: { type: Array, default: () => [] },
	// 'ready' | 'loading' | 'empty' | 'error'
	state: { type: String, default: 'ready' },
	emptyTitle: { type: String, default: 'No analytics for this period' },
	emptyText: { type: String, default: 'No player activity was recorded for the selected content and dates. Try a different date range or selection.' },
	subtitle: { type: String, default: '' },
	// Optional trailing link in the subtitle ("Choose content"); emits `subtitle-link`.
	subtitleLink: { type: String, default: '' },
	note: { type: String, default: '' },
	sort: { type: Object, default: () => ({ column: 'totalHoursWatched', direction: 'desc' }) },
	// Column keys currently being fetched (second-phase columns).
	loadingColumns: { type: Array, default: () => [] },
	// Column keys whose data has been fetched (others show "--").
	loadedColumns: { type: Array, default: () => [] },
	// Column keys whose grouped request failed (header shows a retry control).
	failedColumns: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:sort', 'open', 'retry', 'request-column', 'retry-column', 'subtitle-link']);

const columns = LEADERBOARD_COLUMNS;

function isColumnLoading(key) {
	return props.loadingColumns.includes(key);
}

function isColumnFailed(key) {
	return props.failedColumns.includes(key);
}

function hasValue(row, key) {
	const v = row?.metrics?.[key];
	return v !== null && v !== undefined;
}

function pendingTitle(key) {
	if (isColumnFailed(key)) return 'Data unavailable right now. Click the column header to retry.';
	if (!props.loadedColumns.includes(key)) return 'Loads on demand. Sort by this column to fetch it.';
	return key === 'avgViewTime' ? 'No plays in this period' : 'No data';
}

function format(key, value) {
	return formatMetricValue(key, value);
}

function onSort(column) {
	// A failed column's header is a retry control first; sorting it would only
	// reorder "--" cells.
	if (isColumnFailed(column)) {
		emit('retry-column', column);
		return;
	}
	const direction = props.sort.column === column && props.sort.direction === 'desc' ? 'asc' : 'desc';
	if (!props.loadedColumns.includes(column)) emit('request-column', column);
	emit('update:sort', { column, direction });
}
</script>
